/* PastéBCN — SEO content (bloque desplegable)
   Web Component <pb-seo-content>:
   - Toggle "Leer más / Leer menos" con animación de altura (max-height).
   - Modo `data-teaser='false'`: colapsado a 0 (sin teaser visible). El
     contenido sigue en el DOM (NUNCA display:none) → crawleable.
   - Links del contenido colapsado con tabindex="-1"; al expandir se restauran.
   - aria-expanded en el botón, aria-hidden en el contenido según estado.
   - IntersectionObserver para animación de entrada de la cabecera.
   - Respeta prefers-reduced-motion. */

if (!customElements.get('pb-seo-content')) {
  class PbSeoContent extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.toggleEnabled = this.dataset.toggle !== 'false';
      this.expandLabel = this.dataset.expandLabel || 'Leer más';
      this.collapseLabel = this.dataset.collapseLabel || 'Leer menos';

      this.toggleBtn = this.querySelector('.pb-seo-content__toggle');
      this.toggleLabelEl = this.querySelector('.pb-seo-content__toggle-label');
      this.clip = this.querySelector('.pb-seo-content__clip');
      this.inner = this.querySelector('.pb-seo-content__inner');

      this._observeEntry();

      if (!this.toggleEnabled) {
        this.dataset.expanded = 'true';
        if (this.clip) this.clip.style.maxHeight = 'none';
        if (this.inner) this.inner.setAttribute('aria-hidden', 'false');
        return;
      }

      this._setLinksFocusable(false);

      if (this.toggleBtn) {
        this._onToggle = () => this.toggle();
        this.toggleBtn.addEventListener('click', this._onToggle);
      }
    }

    disconnectedCallback() {
      if (this.toggleBtn && this._onToggle) {
        this.toggleBtn.removeEventListener('click', this._onToggle);
      }
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._onTransitionEnd && this.clip) {
        this.clip.removeEventListener('transitionend', this._onTransitionEnd);
      }
    }

    toggle() {
      if (this.dataset.expanded === 'true') this.collapse();
      else this.expand();
    }

    expand() {
      this.dataset.expanded = 'true';
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
      if (this.toggleLabelEl) this.toggleLabelEl.textContent = this.collapseLabel;
      if (this.inner) this.inner.setAttribute('aria-hidden', 'false');
      this._setLinksFocusable(true);

      if (!this.clip) return;
      if (this.reducedMotion) {
        this.clip.style.maxHeight = 'none';
        return;
      }
      this.clip.style.maxHeight = this.clip.scrollHeight + 'px';
      this._onTransitionEnd = (e) => {
        if (e.propertyName !== 'max-height') return;
        if (this.dataset.expanded === 'true') {
          this.clip.style.maxHeight = 'none';
        }
        this.clip.removeEventListener('transitionend', this._onTransitionEnd);
        this._onTransitionEnd = null;
      };
      this.clip.addEventListener('transitionend', this._onTransitionEnd);
    }

    collapse() {
      this.dataset.expanded = 'false';
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
      if (this.toggleLabelEl) this.toggleLabelEl.textContent = this.expandLabel;
      if (this.inner) this.inner.setAttribute('aria-hidden', 'true');
      this._setLinksFocusable(false);

      if (!this.clip) return;
      if (this.reducedMotion) {
        this.clip.style.maxHeight = '';
        return;
      }
      // De la altura actual (completa) al valor CSS (teaser).
      this.clip.style.maxHeight = this.clip.scrollHeight + 'px';
      // Fuerza reflow para que el navegador registre el valor de partida.
      void this.clip.offsetHeight;
      this.clip.style.maxHeight = '';
    }

    _setLinksFocusable(focusable) {
      if (!this.inner) return;
      this.inner.querySelectorAll('a').forEach((link) => {
        if (focusable) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', '-1');
      });
    }

    _observeEntry() {
      if (this.reducedMotion || typeof IntersectionObserver === 'undefined') {
        this.classList.add('is-visible');
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('is-visible');
              this._observer.disconnect();
            }
          });
        },
        { threshold: 0.15 }
      );
      this._observer.observe(this);
    }
  }

  customElements.define('pb-seo-content', PbSeoContent);
}
